import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { applyAccentHex } from "../lib/event-theme";

export type AccentKey = "flame" | "gold" | "amber" | "rose" | "cyan" | "violet";
export type HeroLayout = "split" | "center" | "stack";

export type Accent = {
  k: AccentKey;
  label: string;
  c1: string;
  c2: string;
  c3: string;
};

export const ACCENTS: Accent[] = [
  { k: "flame",  label: "Flame",  c1: "#F25C1F", c2: "#FF8A3D", c3: "#C43110" },
  { k: "gold",   label: "Gold",   c1: "#E4A43B", c2: "#F6C76A", c3: "#B07514" },
  { k: "amber",  label: "Amber",  c1: "#F59E0B", c2: "#FCD34D", c3: "#B45309" },
  { k: "rose",   label: "Rose",   c1: "#E1556A", c2: "#F07C8D", c3: "#A12F44" },
  { k: "cyan",   label: "Cyan",   c1: "#4FD1E0", c2: "#7CE3EE", c3: "#1D7F8A" },
  { k: "violet", label: "Violet", c1: "#A179FF", c2: "#C1A3FF", c3: "#6A40CC" },
];

type TweaksDefaults = Partial<{ accent: AccentKey; heroLayout: HeroLayout }>;

declare global {
  interface Window {
    TWEAK_DEFAULTS?: TweaksDefaults;
  }
}

type TweaksContextValue = {
  accent: AccentKey;
  heroLayout: HeroLayout;
  panelVisible: boolean;
  setAccent: (k: AccentKey) => void;
  setHeroLayout: (k: HeroLayout) => void;
  showPanel: () => void;
  hidePanel: () => void;
};

const TweaksCtx = createContext<TweaksContextValue | null>(null);

function applyAccentVars(key: AccentKey) {
  const a = ACCENTS.find((x) => x.k === key) ?? ACCENTS[0];
  applyAccentHex(a.c1, a.c2, a.c3);
}

function applyHeroLayoutAttr(layout: HeroLayout) {
  document.body.setAttribute("data-hero-layout", layout);
}

function persistToHost(edits: TweaksDefaults) {
  try {
    window.parent.postMessage({ type: "__edit_mode_set_keys", edits }, "*");
  } catch {
    /* noop — not in a hosted iframe */
  }
}

export function TweaksProvider({ children }: PropsWithChildren) {
  const initial: { accent: AccentKey; heroLayout: HeroLayout } = useMemo(() => {
    const d = (typeof window !== "undefined" && window.TWEAK_DEFAULTS) || {};
    return {
      accent: (d.accent as AccentKey) ?? "flame",
      heroLayout: (d.heroLayout as HeroLayout) ?? "split",
    };
  }, []);

  const [accent, setAccentState] = useState<AccentKey>(initial.accent);
  const [heroLayout, setHeroLayoutState] = useState<HeroLayout>(initial.heroLayout);
  const [panelVisible, setPanelVisible] = useState(false);

  useEffect(() => { applyAccentVars(accent); }, [accent]);
  useEffect(() => { applyHeroLayoutAttr(heroLayout); }, [heroLayout]);

  const setAccent = useCallback((k: AccentKey) => {
    setAccentState(k);
    persistToHost({ accent: k });
  }, []);

  const setHeroLayout = useCallback((k: HeroLayout) => {
    setHeroLayoutState(k);
    persistToHost({ heroLayout: k });
  }, []);

  const showPanel = useCallback(() => setPanelVisible(true), []);
  const hidePanel = useCallback(() => setPanelVisible(false), []);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const d = (e.data || {}) as { type?: string };
      if (d.type === "__activate_edit_mode") setPanelVisible(true);
      if (d.type === "__deactivate_edit_mode") setPanelVisible(false);
    };
    window.addEventListener("message", onMessage);
    try {
      window.parent.postMessage({ type: "__edit_mode_available" }, "*");
    } catch {
      /* noop */
    }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const value = useMemo<TweaksContextValue>(
    () => ({ accent, heroLayout, panelVisible, setAccent, setHeroLayout, showPanel, hidePanel }),
    [accent, heroLayout, panelVisible, setAccent, setHeroLayout, showPanel, hidePanel],
  );

  return <TweaksCtx.Provider value={value}>{children}</TweaksCtx.Provider>;
}

export function useTweaks(): TweaksContextValue {
  const ctx = useContext(TweaksCtx);
  if (!ctx) throw new Error("useTweaks must be used inside <TweaksProvider>");
  return ctx;
}
